import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ActiveConversation {
  id: string;
  customer_request_id: string;
  supplier_request_id: string | null;
  customer_accept: boolean | null;
  confirm_booking: boolean | null;
  confirm_booking_customer: boolean | null;
  last_activity_at: string;
  archived: boolean | null;
  customer_requests: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string | null;
    wedding_date: string | null;
    wedding_location: string | null;
    budget: string | null;
    created_at: string;
    intro_text: string | null;
    images: string[] | null;
    total_suppliers_for_request: number | null;
    direct_request: boolean | null;
    guest_details: any;
    contact_email: boolean;
    contact_whatsapp: boolean;
    contact_call: boolean;
    contact_no_preference: boolean;
    request_type: string | null;
    number_of_hours: string | null;
    special_requests: string | null;
    supplier_categories: {
      id: string;
      name: string;
    } | null;
  } | null;
  supplier_requests: {
    id: string;
    accept: boolean | null;
    archived: boolean | null;
    supplier_pages: {
      name: string | null;
      profile_image_url: string | null;
    } | null;
  } | null;
  unread_count: number;
}

export const useSupplierActiveConversations = (supplierId?: string) => {
  return useQuery({
    queryKey: ["supplier-active-conversations", supplierId],
    queryFn: async (): Promise<ActiveConversation[]> => {
      if (!supplierId) return [];

      // Get conversations where this supplier has sent at least one message
      const { data: conversations, error } = await supabase
        .from("conversations")
        .select(`
          id,
          customer_request_id,
          supplier_request_id,
          customer_accept,
          confirm_booking,
          confirm_booking_customer,
          last_activity_at,
          supplier_last_read_at,
          customer_requests!inner (
            id,
            first_name,
            last_name,
            email,
            phone_number,
            wedding_date,
            wedding_location,
            budget,
            created_at,
            intro_text,
            images,
            total_suppliers_for_request,
            direct_request,
            guest_details,
            contact_email,
            contact_whatsapp,
            contact_call,
            contact_no_preference,
            request_type,
            number_of_hours,
            special_requests,
            supplier_categories!inner (
              id,
              name
            )
          ),
          supplier_requests!inner (
            id,
            accept,
            archived,
            supplier_pages!inner (
              supplier_id,
              name,
              profile_image_url
            )
          )
        `)
        .eq("supplier_requests.supplier_pages.supplier_id", supplierId)
        .eq("supplier_requests.accept", true)
        .eq("customer_accept", true)
        .neq("confirm_booking", true) // Not yet confirmed bookings
        .or("customer_deny.is.null,customer_deny.eq.false")
        .order("last_activity_at", { ascending: false });

      if (error) {
        console.error("Error fetching supplier active conversations:", error);
        throw error;
      }

      if (!conversations) return [];

      // Filter conversations based on direct_request status
      const conversationsWithMessages = await Promise.all(
        conversations.map(async (conversation) => {
          const isDirectRequest = conversation.customer_requests?.direct_request || false;
          
          // For direct requests, require supplier to have sent at least one message
          if (isDirectRequest) {
            const { data: supplierMessages, error: messagesError } = await supabase
              .from("conversation_messages")
              .select("id")
              .eq("conversation_id", conversation.id)
              .eq("sender_role", "supplier")
              .limit(1);

            if (messagesError) return null;

            // Exclude direct request conversations without supplier messages
            if (!supplierMessages || supplierMessages.length === 0) {
              return null;
            }
          }
          
          // For form requests, include immediately (no message check needed)
          // Calculate unread count for customer messages since supplier last read
          const { count: unreadCount, error: unreadError } = await supabase
            .from("conversation_messages")
            .select("*", { count: "exact", head: true })
            .eq("conversation_id", conversation.id)
            .eq("sender_role", "customer")
            .gt("created_at", conversation.supplier_last_read_at || new Date(0).toISOString());

          const unread_count = unreadError ? 0 : (unreadCount || 0);

          return {
            ...conversation,
            unread_count,
            archived: conversation.supplier_requests?.archived || false,
          } as ActiveConversation;
        })
      );

      return conversationsWithMessages.filter((conv): conv is ActiveConversation => conv !== null);
    },
    enabled: !!supplierId,
  });
};